import React from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Badge, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useEffect } from "react"
import { getUserActivityDetails } from "store/actions/commonAction"
import { useSelector } from "react-redux"
import TableShimmer from "components/Shared/TableShimmer"
import BackButton from "components/Shared/BackButton"
import formatActivityDate from "utils/formatActivityDate"
import { useTranslation } from "react-i18next"
import isPermissionEnabled from "common/isPermissionEnabled"
import { getUserActivityTypeLabel } from "constants/userActivityTypes"

const UserActivityDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("User Activity Details ")

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { userActivityLoading, userActivityDetails } = useSelector(
    state => state.Common
  )

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      36,
      "user_activity_1",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [])

  useEffect(() => {
    const payload = {
      id,
    }
    dispatch(getUserActivityDetails(payload))
  }, [])

  const renderTableHeader = () => {
    if (userActivityDetails.oldData || userActivityDetails.newData) {
      return (
        <tr>
          <th>{t("Name")}</th>
          {userActivityDetails.oldData && <th>{t("Old Data")}</th>}
          {userActivityDetails.newData && <th>{t("Updated Data")}</th>}
        </tr>
      )
    }
    return null
  }

  const renderTableRow = (oldData, newData) => {
    // Function to remove keys with empty arrays
    const removeEmptyArrayKeys = data => {
      const result = { ...data }
      Object.keys(result).forEach(key => {
        if (Array.isArray(result[key]) && result[key].length === 0) {
          delete result[key]
        }
      })
      return result
    }

    // Function to perform deep equality check
    const isEqual = (a, b) => {
      if (a === b) return true
      if (
        typeof a !== "object" ||
        typeof b !== "object" ||
        a === null ||
        b === null
      )
        return false
      const keysA = Object.keys(a),
        keysB = Object.keys(b)
      if (keysA.length !== keysB.length) return false
      return keysA.every(key => isEqual(a[key], b[key]))
    }

    // Remove keys with empty arrays from both oldData and newData
    const oldDataFiltered = removeEmptyArrayKeys(oldData)
    const newDataFiltered = removeEmptyArrayKeys(newData)

    // Combine keys from oldData and newData
    const keys = Array.from(
      new Set([
        ...Object.keys(oldDataFiltered || {}),
        ...Object.keys(newDataFiltered || {}),
      ])
    )

    // Filter out keys where the values are the same in oldData and newData
    const changedKeys = keys.filter(
      key => !isEqual(oldDataFiltered?.[key], newDataFiltered?.[key])
    )

    // Map over the changed keys and render the differences
    return changedKeys.map((key, index) => {
      const newValue = newDataFiltered[key]
      const oldValue = oldDataFiltered[key]

      // Convert values to strings for display, using formatDate for 'updatedAt'
      const getDisplayValue = value => {
        if (key === "updatedAt") {
          return formatDate(value)
        }
        if (key === "status") {
          return value === 1 ? "Active" : "Deactive"
        }
        return typeof value === "object"
          ? JSON.stringify(value, null, 2)
          : String(value)
      }

      const newValueString = getDisplayValue(newValue)
      const oldValueString = getDisplayValue(oldValue)

      return (
        <tr key={key}>
          <td className="text-capitalize">
            {key
              ?.replace(/([a-z])([A-Z])/g, "$1 $2")
              ?.replace(/_/g, " ")
              ?.toLowerCase()}
          </td>
          {oldValue !== undefined && (
            <td style={{ maxWidth: "400px", whiteSpace: "pre-wrap" }}>
              <p className="text-danger">
                {oldValue !== undefined ? oldValueString : "-"}
              </p>
            </td>
          )}
          {newValue !== undefined && (
            <td style={{ maxWidth: "400px", whiteSpace: "pre-wrap" }}>
              <p className="text-success">
                {newValue !== undefined ? newValueString : "-"}
              </p>
            </td>
          )}
        </tr>
      )
    })
  }

  const formatDate = utcDateString => formatActivityDate(utcDateString, language)

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton /> {t("User Activity Details")}
                </CardTitle>
                <div className="table-responsive">
                  {userActivityLoading ? (
                    <TableShimmer row={4} col={2} />
                  ) : (
                    <Table className="table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("User Id")}</th>
                          <td>{userActivityDetails?.user?._id || "-"}</td>
                        </tr>
                        <tr>
                          <th>{t("Name")}</th>
                          <td>
                            {userActivityDetails?.user?.firstName || "-"}{" "}
                            {userActivityDetails?.user?.lastName}
                          </td>
                        </tr>
                        {/* <tr>
                          <th>{t("Username")}</th>
                          <td>{userActivityDetails?.user?.username || "-"}</td>
                        </tr> */}

                        <tr>
                          <th>{t("Email")}</th>
                          <td>{userActivityDetails?.user?.email || "-"}</td>
                        </tr>
                        <tr>
                          <th>{t("Reference Id")}</th>
                          <td>{userActivityDetails?.referenceId || "-"}</td>
                        </tr>
                        <tr>
                          <th>{t("Activity")}</th>
                          <td>
                            <span className="user-activity-message">
                              <span className="user-activity-message__text">
                                {language === "sa"
                                  ? userActivityDetails.messageAr
                                  : userActivityDetails.message}
                              </span>{" "}
                              <span
                                className="user-activity-message__time"
                                dir={language === "sa" ? "rtl" : "ltr"}
                              >
                                {t("activityLogOn")}{" "}
                                {formatDate(userActivityDetails.createdAt)}
                              </span>
                            </span>
                          </td>
                        </tr>

                        <tr>
                          <th>{t("Activity On")}</th>
                          <td>
                            {getUserActivityTypeLabel(
                              userActivityDetails?.type,
                              t
                            ) || "-"}
                          </td>
                        </tr>
                        <tr>
                          <th>{t("Activity Type")}</th>
                          <td>{t(userActivityDetails?.actionType) || "-"}</td>
                        </tr>

                        <tr>
                          <th>{t("Status")}</th>
                          <td>
                            {userActivityDetails?.status === 1 ? (
                              <Badge className="bg-success">
                                {t("Active")}
                              </Badge>
                            ) : (
                              (
                                <Badge className="bg-danger">
                                  {t("Deactive")}
                                </Badge>
                              ) || "-"
                            )}
                          </td>
                        </tr>
                      </thead>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle>{t("Activity Details")}</CardTitle>
                <div className="table-responsive">
                  {userActivityLoading ? (
                    <TableShimmer row={7} col={4} />
                  ) : Object.values(userActivityDetails).length === 0 ? (
                    <p className="text-center">{t("No activity found")}</p>
                  ) : (
                    <Table className="table-nowrap mb-0">
                      <tbody>
                        {renderTableHeader()}
                        {renderTableRow(
                          userActivityDetails.oldData,
                          userActivityDetails.newData
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default UserActivityDetails
